import { useState, useEffect, useCallback } from 'react';

/**
 * Custom hook for managing notifications
 * @param {Object} userInfo - User information object
 * @returns {Object} Notifications state and methods
 */
export const useNotifications = (userInfo) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    
  
    if (!userInfo?.userId) {
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/notifications`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });



      if (response.ok) {
        const data = await response.json();
        // Backend commonly wraps payload as { success, data: { notifications, unreadCount } }
        const payload = data?.data || data;
        setNotifications(payload?.notifications || []);
        setUnreadCount(payload?.unreadCount || 0);
      } else {
        const errorText = await response.text();
      }
    } catch (error) {
      console.error('❌ useNotifications: Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [userInfo?.userId]);

  // Mark a specific notification as read
  const markAsRead = useCallback(async (notificationId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}), // Send empty JSON object
      });

      if (response.ok) {
        const data = await response.json();
      
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === notificationId 
              ? { ...notif, isRead: true }
              : notif
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } else {
        const errorText = await response.text();
        console.error('❌ useNotifications: Failed to mark notification as read:', response.status, errorText);
      }
    } catch (error) {
      console.error('❌ useNotifications: Error marking notification as read:', error);
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/notifications/mark-all-read`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}), // Send empty JSON object
      });

      if (response.ok) {
        const data = await response.json();
        setNotifications(prev => 
          prev.map(notif => ({ ...notif, isRead: true }))
        );
        setUnreadCount(0);
      } else {
        const errorText = await response.text();
        console.error('❌ useNotifications: Failed to mark all notifications as read:', response.status, errorText);
      }
    } catch (error) {
      console.error('❌ useNotifications: Error marking all notifications as read:', error);
    }
  }, []);

  // Set up polling for new notifications
  useEffect(() => {
    
    fetchNotifications();
    
    // Listen for custom event to refresh notifications immediately
    const handleNotificationsUpdated = () => {
      fetchNotifications();
    };
    
    window.addEventListener('notificationsUpdated', handleNotificationsUpdated);
    
    // Set up polling for new notifications (every 30 seconds)
    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('notificationsUpdated', handleNotificationsUpdated);
    };
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead
  };
};
